'use client';

import { useId, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import {
  CalendarCheck,
  CreditCard,
  MessageCircle,
  ReceiptText,
  UserPlus,
} from 'lucide-react';

import { cn } from '@kit/ui/utils';

import type { MarketingLocale } from '~/lib/marketing/site';

import { HERO_EVENTS, HOME_HERO } from '../../_lib/platform.content';
import { CalendarDemo } from './calendar-demo';
import { ClassBookingDemo } from './class-booking-demo';
import { CourtBookingDemo } from './court-booking-demo';
import { ClinicWhatsAppDemo } from './whatsapp-demo';
import { ADMIN_DEMO_SIZE, PHONE_SIZE } from './demo-sizes';
import {
  DemoMotionProvider,
  DemoPauseButton,
  DemoStage,
  EASE_OUT_EXPO,
  FitScale,
  SPRING_SNAPPY,
  SPRING_SOFT,
  useDemoTimeline,
} from './demo-kit';

/*
 * Landing hero composition: the member app in front (alternating between a
 * court booking and a class booking so every audience sees itself), the
 * admin calendar behind it, and a live feed of what the platform just did.
 * Desktop gets the layered scene; small screens get the phone alone.
 */

const FEED_DURATIONS = [2600, 2600, 2600, 2600] as const;

const MODES = [
  { key: 'appointments', Demo: ClinicWhatsAppDemo },
  { key: 'classes', Demo: ClassBookingDemo },
  { key: 'courts', Demo: CourtBookingDemo },
] as const;

const EVENT_ICONS = {
  whatsapp: { Icon: MessageCircle, tone: 'bg-[#1faa59] text-white' },
  class: { Icon: CalendarCheck, tone: 'bg-[#f5ebe5] text-[#9a5b43]' },
  payment: { Icon: CreditCard, tone: 'bg-[#ddf5ec] text-[#2f7a63]' },
  invoice: { Icon: ReceiptText, tone: 'bg-[#ede8e2] text-[#202020]' },
  member: { Icon: UserPlus, tone: 'bg-[#e6f0ff] text-[#2c4c80]' },
} as const;

const NOW_LABEL = { en: 'now', ar: 'الآن' } as const;

const FEED_LABEL = {
  en: 'Live feed of platform events: bookings over WhatsApp, classes booked, payments received, e-invoices issued and new members.',
  ar: 'موجز مباشر لأحداث المنصة: حجوزات عبر واتساب وحصص محجوزة ومدفوعات مستلمة وفواتير إلكترونية وأعضاء جدد.',
} as const;

/** 44px switch plus its 12px gap to the phone. */
const SWITCH_HEIGHT = 56;
const PHONE_COLUMN = {
  width: PHONE_SIZE.width,
  height: PHONE_SIZE.height + SWITCH_HEIGHT,
} as const;
const SCENE = { width: 660, height: 690 } as const;
const CALENDAR_SCALE = 0.74;

export function HeroDemo({ locale }: { locale: MarketingLocale }) {
  const [paused, setPaused] = useState(false);
  const pauseButton = (
    <DemoPauseButton
      paused={paused}
      onToggle={() => setPaused((value) => !value)}
      locale={locale}
      className="absolute end-0 top-0 z-20"
    />
  );

  return (
    <DemoMotionProvider paused={paused}>
      <div className="relative hidden md:block">
        {pauseButton}
        <FitScale width={SCENE.width} height={SCENE.height}>
          <div className="relative" style={SCENE}>
            <div
              className="absolute end-0 top-24"
              style={{
                width: ADMIN_DEMO_SIZE.width * CALENDAR_SCALE,
                height: ADMIN_DEMO_SIZE.height * CALENDAR_SCALE,
              }}
            >
              <div
                className="origin-top-left rtl:origin-top-right"
                style={{
                  width: ADMIN_DEMO_SIZE.width,
                  transform: `scale(${CALENDAR_SCALE})`,
                }}
              >
                <CalendarDemo locale={locale} />
              </div>
            </div>
            <div className="absolute bottom-0 start-4">
              <MemberAppSwitcher locale={locale} />
            </div>
            <div className="absolute bottom-10 end-2 w-[270px]">
              <EventFeed locale={locale} />
            </div>
          </div>
        </FitScale>
      </div>

      <div className="relative md:hidden">
        {pauseButton}
        <FitScale width={PHONE_COLUMN.width} height={PHONE_COLUMN.height}>
          <MemberAppSwitcher locale={locale} />
        </FitScale>
      </div>
    </DemoMotionProvider>
  );
}

/**
 * Appointments / Classes / Courts switch above the phone. It advances when the current demo
 * reports a finished loop, so a flow is never cut off mid-way; a click pins
 * the visitor's choice.
 */
function MemberAppSwitcher({ locale }: { locale: MarketingLocale }) {
  const [state, setState] = useState({ mode: 0, pinned: false, run: 0 });
  const layoutScope = useId();
  const { Demo } = MODES[state.mode]!;

  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const advance = () =>
    setState((prev) =>
      prev.pinned
        ? prev
        : { ...prev, mode: (prev.mode + 1) % MODES.length, run: prev.run + 1 },
    );

  const pin = (mode: number) =>
    setState((prev) => ({ mode, pinned: true, run: prev.run + 1 }));

  return (
    <div className="flex flex-col items-center" style={PHONE_COLUMN}>
      <div
        role="tablist"
        aria-label={HOME_HERO.modes.label[locale]}
        onKeyDown={(event) => {
          if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            const next = (state.mode + 1) % MODES.length;
            pin(next);
            tabRefs.current[next]?.focus();
          }
        }}
        className="flex h-11 items-center gap-1 rounded-full bg-white/10 p-1 ring-1 ring-white/15 backdrop-blur"
      >
        {MODES.map(({ key }, index) => {
          const selected = index === state.mode;

          return (
            <button
              key={key}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              type="button"
              role="tab"
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => pin(index)}
              className={cn(
                'relative inline-flex h-9 items-center rounded-full px-3.5 font-roboto text-[13px] font-medium transition-colors',
                selected ? 'text-[#202020]' : 'text-white/75 hover:text-white',
              )}
            >
              {selected && (
                <motion.span
                  layoutId={`${layoutScope}-mode`}
                  className="absolute inset-0 rounded-full bg-white"
                  transition={SPRING_SNAPPY}
                />
              )}
              <span className="relative">{HOME_HERO.modes[key][locale]}</span>
            </button>
          );
        })}
      </div>

      <div className="relative mt-3" style={PHONE_SIZE}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={`${state.run}-${state.mode}`}
            initial={{ opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.45, ease: EASE_OUT_EXPO }}
          >
            <Demo locale={locale} onLoop={advance} />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function EventFeed({ locale }: { locale: MarketingLocale }) {
  const stageRef = useRef<HTMLDivElement>(null);
  const timeline = useDemoTimeline(stageRef, FEED_DURATIONS);
  const latest = timeline.cycle * FEED_DURATIONS.length + timeline.step;
  const visible = [latest, latest - 1, latest - 2].filter((n) => n >= 0);

  return (
    <DemoStage ref={stageRef} timeline={timeline} label={FEED_LABEL[locale]}>
      <div className="relative flex flex-col gap-2.5">
        <AnimatePresence initial={false} mode="popLayout">
          {visible.map((n, position) => {
            const event = HERO_EVENTS[n % HERO_EVENTS.length]!;
            const { Icon, tone } = EVENT_ICONS[event.kind];

            return (
              <motion.div
                key={n}
                layout
                initial={{ opacity: 0, y: -18, scale: 0.94 }}
                animate={{ opacity: 1 - position * 0.28, y: 0, scale: 1 - position * 0.03 }}
                exit={{ opacity: 0, scale: 0.92, transition: { duration: 0.25 } }}
                transition={SPRING_SOFT}
                className="flex items-center gap-3 rounded-[16px] border border-white/60 bg-white/95 px-3.5 py-3 shadow-[0_20px_40px_-20px_rgba(0,0,0,0.5)] backdrop-blur"
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] ${tone}`}>
                  <Icon className="h-[18px] w-[18px]" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-[#202020]">
                    {event.title[locale]}
                  </span>
                  <span className="block truncate text-[11.5px] text-[#8a857c]">
                    {event.detail[locale]}
                  </span>
                </span>
                <span className="self-start font-roboto-mono text-[10px] text-[#8a857c]">
                  {NOW_LABEL[locale]}
                </span>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </DemoStage>
  );
}
